let Pubnub;

/**
 * It creates the config object for Pubnub creation
 * @return {Object} Pubnub config
 */
const getPubnubConfig = () => ({
  restore: true,
  heartbeatInterval: 0,
});

/**
 * It creates a Pubnub instance
 * @param {Object} config Config object to create Pubnub instance
 * @param {String} config.uuid Unique user session's identifier on Pubnub
 * @param {String} config.authKey Key to communicate with private channels in Pubnub
 * @param {String} config.publishKey Key to be able to publish in Pubnub
 * @param {String} config.subscribeKey Key to be able to subscribe to Pubnub's channels
 * @return {Promise} Promise with a Pubnub instance
 */
export async function createInstance({ uuid, authKey, publishKey, subscribeKey }) {
  if (!Pubnub) {
    Pubnub = (await import("pubnub"))?.default;
  }

  if (!Pubnub) {
    throw new Error("Couldn't import Pubnub's SDK.");
  }

  if (!uuid) {
    throw new Error("The uuid is required to create a Pubnub instance.");
  }

  if (!authKey) {
    throw new Error("The authKey is required to create a Pubnub instance.");
  }

  if (!publishKey) {
    throw new Error("The publishKey is required to create a Pubnub instance.");
  }

  if (!subscribeKey) {
    throw new Error("The subscribeKey is required to create a Pubnub instance.");
  }

  return new Pubnub({
    ...getPubnubConfig(),
    uuid,
    authKey,
    publishKey,
    subscribeKey,
  });
}

/**
 * It publishes a message, given a channel name and a content
 * @param {Object} config Object to send a Pubnub message
 * @param {Object} config.pubnubInstance Instance of Pubnub to perform the action
 * @param {String} config.channel Name of the channel to send the message to
 * @param {any} config.message Content to be sent as message
 * @param {Object} config.options Object containing optional attributes for the publish
 */
export async function publish({ pubnubInstance, channel, message, options = {} }) {
  if (!pubnubInstance) {
    throw new Error("You need to create a Pubnub instance before publishing messages.");
  }

  if (!channel || !message) {
    throw new Error("Channel and message are required for publishing messages.");
  }

  return pubnubInstance.publish({
    channel,
    message,
    ...options,
  });
}

/**
 * It subscribes to channels, so that listeners can be registered and triggered for the subscribed channels communication
 * @param {Object} config Object to subscribe to Pubnub channels
 * @param {Object} config.pubnubInstance Instance of Pubnub to perform the action
 * @param {Array} config.channels List of channels to subscribe to
 * @param {Boolean} config.withPresence Automatically subscribe to presence channel
 */
export function subscribe({ pubnubInstance, channels, withPresence = false }) {
  if (!pubnubInstance) {
    throw new Error("You need to create a Pubnub instance before publishing messages.");
  }

  if (!Array.isArray(channels)) {
    throw new Error("Channels are required for subscribing to it.");
  }

  pubnubInstance.subscribe({ channels, withPresence });
}

/**
 * It registers a listener for incoming messages on the subscribed channels
 * @param {Object} config Object to add a listener to Pubnub messages
 * @param {Object} config.pubnubInstance Instance of Pubnub to perform the action
 * @param {Object} config.listener Object with type of listener to register
 */
export function addListener({ pubnubInstance, listener }) {
  if (!pubnubInstance) {
    throw new Error("You need to create a Pubnub instance before publishing messages.");
  }

  if (typeof listener !== "object") {
    throw new Error("Listener is required for registering it.");
  }

  pubnubInstance.addListener(listener);
}

/**
 * It gets the current occupancy of a channel
 * @param {Object} config Object to add a listener to Pubnub messages
 * @param {Object} config.pubnubInstance Instance of Pubnub to perform the action
 * @param {String} config.channelName Name of the channel to check occupancy
 */
export async function getChannelOccupancy({ pubnubInstance, channelName }) {
  if (!pubnubInstance) {
    throw new Error("You need to create a Pubnub instance before checking occupancy.");
  }

  if (!channelName?.length) {
    throw new Error("Channel name is required for checking occupancy.");
  }

  return pubnubInstance.hereNow({
    channels: [channelName],
  });
}
